import React, { ReactNode, useEffect, useState } from 'react';
import { InformationType } from '../types';
import { Message, MESSAGE_TRANSITION_MS, MessageRoot, MessageViewport } from './input.styles';

interface InputMessageProps {
    /** Referenced by the field's aria-describedby while a message is shown. */
    id: string;
    message?: ReactNode;
    informationType: InformationType;
}

/**
 * The line under a field that carries its error or info message, easing in and out rather than
 * snapping.
 *
 * <p>TextInput, MultiSelect and Dropdown each mounted their message conditionally, so a message
 * appearing inserted a line and pushed every field below it down the form. That jump is why callers
 * drew their own messages instead of using this one. The line now opens from zero height with the
 * grid-template-rows 0fr to 1fr transition, the one way to animate to a height nothing has measured.
 *
 * <p>The last message is kept while the line collapses, so the text fades out with its space rather
 * than vanishing before it — and only while: it is released once the collapse has finished. A
 * message can be any ReactNode, and one kept for good would leave its effects running and any
 * portal it opened on screen, since a portal is not a descendant of the hidden line. The viewport carries no padding or margin of its own: 0fr collapses a
 * grid item's content box, and any spacing on the item itself would stay behind as a permanent gap.
 */
export function InputMessage({ id, message, informationType }: InputMessageProps) {
    // Truthiness, to match the aria-describedby each field already computes from the same prop.
    const shown = Boolean(message);

    // Remembered from the last render that had a message, per React's "storing information from
    // previous renders" pattern, so a clearing message has something to fade out.
    const [last, setLast] = useState({ message, informationType });
    if (shown && (message !== last.message || informationType !== last.informationType)) {
        setLast({ message, informationType });
    }

    /*
     * Release the kept message once the line has finished collapsing, so its subtree unmounts. The
     * state is set from the timer's callback, not the effect's body. Showing a message again inside
     * the window cancels the release.
     */
    useEffect(() => {
        if (shown || !last.message) return undefined;
        const release = setTimeout(
            () => setLast((current) => ({ ...current, message: undefined })),
            MESSAGE_TRANSITION_MS
        );
        return () => clearTimeout(release);
    }, [shown, last]);

    const text = shown ? message : last.message;
    const type = shown ? informationType : last.informationType;

    return (
        <MessageRoot $shown={shown} aria-hidden={!shown}>
            <MessageViewport>
                {text ? (
                    <Message id={id} $informationType={type}>
                        {text}
                    </Message>
                ) : null}
            </MessageViewport>
        </MessageRoot>
    );
}
